import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, ArrowLeft, Home, MessageSquare } from 'lucide-react';
import { THEMES, DEFAULT_LOGO_URL } from '../components/landing/landingConstants';
import { Button } from '../components/ui/button';

/**
 * Branded 404 page.
 *
 * Football-themed copy ("Own Goal", "Off Target", "VAR review"). Mirrors the
 * landing-page theme system so it switches dark/light with the same toggle
 * users already know. Surfaces the path that wasn't found so users (and our
 * logs) can see what they typed wrong.
 */
export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'dark');
  const isDark = theme === 'dark';
  const t = THEMES[theme];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const toggleTheme = () => {
    const newTheme = isDark ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('landingTheme', newTheme);
  };

  // Determine where "back to action" should send them. Logged-in users get
  // the dressing room; everyone else gets the landing page.
  const isLoggedIn = !!localStorage.getItem('token');
  const homePath = isLoggedIn ? '/dressing-room' : '/';
  const homeLabel = isLoggedIn ? 'Back to the dressing room' : 'Back to the touchline';

  return (
    <div
      className={`min-h-screen ${t.bg} ${t.text} relative overflow-hidden`}
      data-testid="not-found-page"
    >
      <style>{`
        @keyframes ref-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }
        .ref-img { animation: ref-pulse 3.2s ease-in-out infinite; }

        /* Stadium-style stripe behind the digits — subtle pitch reference. */
        .pitch-stripe::before {
          content: "";
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            90deg,
            rgba(234, 179, 8, 0.06) 0px,
            rgba(234, 179, 8, 0.06) 60px,
            transparent 60px,
            transparent 120px
          );
          pointer-events: none;
        }
      `}</style>

      {/* Theme toggle (matches landing/legal pages) */}
      <button
        onClick={toggleTheme}
        className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${
          isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
        }`}
        aria-label="Toggle theme"
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Logo top-left, clickable home */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 group"
        data-testid="not-found-logo"
      >
        <img
          src={DEFAULT_LOGO_URL}
          alt="Grassroots Coaching"
          className="w-10 h-10 object-contain group-hover:opacity-80 transition-opacity"
        />
        <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} hidden sm:inline`}>
          Grassroots Coaching
        </span>
      </button>

      {/* Hero block */}
      <main className="relative pitch-stripe min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-3xl w-full text-center relative z-10">
          {/* Pre-headline match-card style label */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-xs font-semibold tracking-widest uppercase text-yellow-500">
              Match report · 404
            </span>
          </div>

          {/* Referee with yellow card visual */}
          <div className="flex items-center justify-center mb-8 select-none">
            <img
              src="https://customer-assets.emergentagent.com/job_b281913e-4091-4a90-899a-c0e767ce9196/artifacts/lherc2if_Untitled%20design%20-%202026-06-05T193523.573.png"
              alt="Referee showing a yellow card"
              className="w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] object-cover rounded-full ref-img"
              draggable="false"
              data-testid="not-found-referee-img"
            />
          </div>

          {/* Yellow Card headline */}
          <h1 className={`text-4xl sm:text-6xl font-black tracking-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Yellow Card.
          </h1>

          <p className={`text-base sm:text-lg max-w-xl mx-auto mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Steady on — you've wandered offside. That page isn't on our team sheet.
          </p>
          <p className={`text-sm max-w-xl mx-auto mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            The ref's had a word, jotted it in the book, and waved you back onside.
          </p>

          {/* Mis-typed URL ("offside line") */}
          {location.pathname && location.pathname !== '/' && (
            <div className={`inline-block mb-8 px-4 py-2 rounded-md font-mono text-xs sm:text-sm border ${
              isDark
                ? 'bg-gray-900/50 border-gray-700 text-gray-400'
                : 'bg-gray-100 border-gray-200 text-gray-500'
            }`} data-testid="not-found-path">
              You tried: <span className={isDark ? 'text-white' : 'text-gray-900'}>{location.pathname}</span>
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate(homePath)}
              className="bg-[#007a33] hover:bg-[#005c26] text-white px-6"
              data-testid="not-found-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              {homeLabel}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate(-1)}
              className={isDark ? 'border-gray-700 text-white hover:bg-gray-800' : ''}
              data-testid="not-found-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to the previous play
            </Button>
          </div>

          {/* Soft secondary link */}
          <p className={`text-xs mt-10 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Need a hand?{' '}
            <button
              onClick={() => window.location.href = 'mailto:support@grassrootscoaching.app?subject=Lost%20on%20a%20404'}
              className="inline-flex items-center gap-1 text-[#007a33] hover:underline font-medium"
              data-testid="not-found-support-link"
            >
              <MessageSquare className="w-3 h-3" />
              Have a word with the gaffer
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
